import Sidebar from '@/components/layout/sidebar';
import TaskHelpRequests from '@/components/task-help-requests';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { isUnauthorizedError } from '@/lib/authUtils';
import { apiRequest } from '@/lib/queryClient';
import { useQuery } from '@tanstack/react-query';
import { HelpCircle, PanelLeftClose, PanelLeftOpen, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function HelpRequestsPage() {
  const { toast } = useToast();
  const { user } = useAuth();

  // Sidebar collapse state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  // Load sidebar collapse state from localStorage
  useEffect(() => {
    if (!user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored === 'true' || stored === 'false') {
      setIsSidebarCollapsed(stored === 'true');
    }
  }, [user?.id]);

  // Save sidebar collapse state to localStorage
  useEffect(() => {
    if (!user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    window.localStorage.setItem(key, String(isSidebarCollapsed));
  }, [isSidebarCollapsed, user?.id]);

  const { data: currentUser } = useQuery({
    queryKey: ['/api/users/me'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/users/me');
      return response;
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
    },
  });

  return (
    <div className="flex h-screen bg-gray-50">
      {!isSidebarCollapsed && <Sidebar />}

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header with Sidebar Toggle */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              {/* Sidebar Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="p-2 h-8 w-8 flex-shrink-0"
                aria-label={
                  isSidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'
                }
              >
                {isSidebarCollapsed ? (
                  <PanelLeftOpen className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </Button>

              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center">
                  <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-blue-600 flex-shrink-0" />
                  <span className="truncate">Task Help Requests</span>
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm hidden sm:block">
                  View tasks where help has been requested or provided
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            <Tabs defaultValue="help-asked" className="space-y-4 sm:space-y-6">
              <TabsList className="grid w-full grid-cols-2 h-auto">
                <TabsTrigger
                  value="help-asked"
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2 px-2 sm:px-4"
                >
                  <HelpCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline">Help Asked</span>
                  <span className="xs:hidden">Asked</span>
                </TabsTrigger>
                <TabsTrigger
                  value="help-done"
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2 px-2 sm:px-4"
                >
                  <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline">Help Done</span>
                  <span className="xs:hidden">Done</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="help-asked">
                <Card>
                  <CardHeader className="pb-3 sm:pb-6">
                    <CardTitle className="text-base sm:text-lg">
                      Help Asked Tasks
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                      Tasks where help has been requested from team members
                    </p>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <TaskHelpRequests
                      userId={user?.id}
                      showOnlyStatus="need_help"
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="help-done">
                <Card>
                  <CardHeader className="pb-3 sm:pb-6">
                    <CardTitle className="text-base sm:text-lg">
                      Help Done Tasks
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                      Tasks where help has been provided and marked as complete
                    </p>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <TaskHelpRequests
                      userId={user?.id}
                      showOnlyStatus="help_done"
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
